import {
    ActivityLevelBadge,
} from "@common/chapters/ActivityLevelBadge"
import TextM from "@common/components/text/TextM"
import IconButtonM from "@common/components/button/IconButtonM"
import { IconImageM } from "@common/components/icons/IconsM"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { createColorVariation } from "@data/utility/Utils"
import { FontAwesome5 } from "@expo/vector-icons"
import React, { useContext, useEffect, useRef, useState } from "react"
import { ScrollView, View, Pressable } from "react-native"
import { ActivityIndicator, Switch } from "react-native-paper"
import { ModalM } from "@common/components/modal/ModalM"
import { BulletTextM } from "@common/components/text/BulletTextM"
import { ChampionImageM } from "../../../student/champions/ChampionImageM"
import MaterialContentDisplay from "../../../common/components/material/MaterialContentDisplay"
import { IS_WEB } from "../../../common/utils/mobileUtils"
import CreateActivityButton from "./CreateActivityButton"

// Legacy variation labels for the similar-exo flow (and other proposition flows
// like concept_review / method_review / first_sub_exercise). The 3-cards flow
// no longer uses the top-left badge for its variation hint — see
// computeBatchTypeIndicator below for the type/format chip that sits under the
// teal skill_part bar.
const VARIATION_DIRECTIVE_LABELS = {
    faithful: "Énoncé fidèle",
    other_framing: "Autre formulation",
    other_context: "Autre contexte",
}

// Map the AI's exercise_type (snake_case) to a short FR label displayed in the
// "Type N · {format}" indicator. Unknown values are derived from the
// underscore-snake_case (capitalize first word, replace _ with space).
const EXERCISE_TYPE_LABELS = {
    calcul_pur: "Calcul",
    qcm: "QCM",
    probleme_ouvert: "Problème ouvert",
    vrai_faux: "Vrai/faux",
    mise_en_situation: "Mise en situation",
    fiche_revision: "Fiche de révision",
    exercice_redactionnel: "Rédaction",
    construction_geometrique: "Construction géométrique",
    analyse_erreur: "Analyse d'erreur",
    text_a_trous: "Texte à trous",
    texte_a_trous: "Texte à trous",
}

const formatExerciseTypeLabel = (raw) => {
    if (!raw || typeof raw !== "string") return null
    const key = raw.toLowerCase().trim()
    if (!key) return null
    if (EXERCISE_TYPE_LABELS[key]) return EXERCISE_TYPE_LABELS[key]
    // Generic fallback: first word capitalized, underscores → spaces.
    const spaced = key.replace(/_/g, " ").trim()
    if (!spaced) return null
    return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

// Truncate the verbose `format_directive` from EXERCISE_FORMAT_PALETTES into a
// short, chip-friendly label (≤ 25 chars). The directive is verbose like
// "QCM avec distracteurs piégeux" or "transformation (passif/actif, temps…)".
// Rules:
//   1. Cut at the first " (" or ":" or " — " or " / " — keep only the head.
//   2. Trim trailing punctuation/whitespace.
//   3. Capitalize the first letter.
//   4. If still > 25 chars, truncate with an ellipsis.
const MAX_CHIP_LABEL_LENGTH = 25
const formatChipLabel = (directive) => {
    if (!directive || typeof directive !== "string") return null
    let label = directive.trim()
    if (!label) return null

    // Cut at first delimiter that introduces a parenthetical / sub-clause /
    // alternative. Order matters: " — " before " / " so Français
    // "vrai/faux argumenté — justifier…" cuts at the em-dash.
    const cutMarkers = [" (", " — ", " - ", ": ", ":"]
    for (const marker of cutMarkers) {
        const idx = label.indexOf(marker)
        if (idx > 0) {
            label = label.slice(0, idx)
            break
        }
    }

    // Trim trailing punctuation / whitespace left by the cut.
    label = label.replace(/[\s.,;:—\-/]+$/g, "").trim()
    if (!label) return null

    // Capitalize first letter (preserves "QCM" since it's already uppercase).
    label = label.charAt(0).toUpperCase() + label.slice(1)

    if (label.length > MAX_CHIP_LABEL_LENGTH) {
        label = label.slice(0, MAX_CHIP_LABEL_LENGTH - 1).trimEnd() + "…"
    }
    return label
}

// Resolve the chip suffix label, preferring the persisted `format_directive`
// (verbose, accurate) over the AI's coarse `exercise_type` enum. Falls back
// to null when neither is available — the BatchTypeIndicator then renders
// just "Type N" without a suffix.
const resolveFormatChipLabel = (material) => {
    if (!material) return null
    const directiveLabel = formatChipLabel(material.format_directive)
    if (directiveLabel) return directiveLabel
    return formatExerciseTypeLabel(material.exercise_type)
}

/**
 * Legacy badge label for non-3-card flows (similar-exo + propositions).
 * Returns the existing copy verbatim — no change in behaviour.
 */
const computeLegacyVariationLabel = (material) => {
    if (!material) return ""
    const variation = material.variation
    if (variation === 'concept_review') return "Révision notions clés"
    if (variation === 'method_review') return "Révision méthode"
    if (variation === 'first_sub_exercise') {
        return material.num_clusters
            ? `Exercice en ${material.num_clusters} étapes`
            : "Premier exercice"
    }
    if (variation === 'more_difficult') return "Plus difficile"
    if (variation === 'same_difficulty') return "Même type"
    if (variation === 'same_difficulty_diversified') return "Autre type"
    if (variation === 'other_concepts') return "Autre compétence"
    return ""
}

/**
 * Compute the "Type N · {format}" indicator that sits under the teal skill_part
 * bar for the 3-cards generate flow. Returns null when no chip should render.
 *
 * Rules (per validated design):
 *  - All 3 cards share the same skill_part (1-chip case) → "Type 1/2/3" on
 *    each card (lowest cardIndex = Type 1).
 *  - 2 cards share a skill_part, 1 alone (2-chip case) → "Type 1/2" on the
 *    sharing pair, nothing on the alone card.
 *  - All 3 cards have unique skill_parts (0-chip random / 3-chip) → null.
 *
 * Format suffix derives from `material.format_directive` (the verbose palette
 * directive sent to the AI for this batch — most accurate), with a fallback
 * to the coarse `material.exercise_type` enum. When neither is available,
 * only "Type N" is shown.
 */
const computeBatchTypeIndicator = (material, materials) => {
    if (!material || !Array.isArray(materials)) return null
    // Legacy variation flows handle their own badge — no type indicator.
    if (material.variation) return null

    const myPart = (material.skill_parts && material.skill_parts[0]) || null
    if (!myPart || !myPart.id) return null

    const myCardIndex = material.skills_part
    if (!myCardIndex) return null

    // Cards sharing the same skill_part (including self), sorted by card_index
    // so peer rank 0 = "Type 1".
    const peers = materials
        .filter((m) => m && m.skill_parts && m.skill_parts[0]?.id === myPart.id)
        .sort((a, b) => (a.skills_part || 0) - (b.skills_part || 0))
    if (peers.length < 2) return null

    const peerRank = peers.findIndex((m) => (m.skills_part || 0) === myCardIndex)
    if (peerRank < 0) return null

    const typeNumber = peerRank + 1
    // Keep the chip generic — format/exo type is our internal cuisine, not
    // useful info for the tutor. The actual differentiation is visible in
    // the body content. Persistence of the format directive still happens
    // server-side via structured_data['format_directive'] for analytics.
    return `Type ${typeNumber}`
}

// Compact "Type N" chip — sits inline next to the difficulty badge and any
// legacy variation badge. Subtle primary tint so it reads as metadata.
// Renders nothing when label is null.
const BatchTypeIndicator = ({ label }) => {
    if (!label) return null
    return (
        <View
            style={{
                backgroundColor: theme.colors.primaryLighter,
                borderRadius: 4,
                paddingHorizontal: 6,
                paddingVertical: 3,
                alignItems: "center",
                justifyContent: "center",
                minHeight: 20,
            }}
        >
            <TextM
                fontSize={10}
                fontWeight="Medium"
                color={theme.colors.primary}
                numberOfLines={1}
            >
                {label}
            </TextM>
        </View>
    )
}

/**
 * Horizontal scrollable cards displaying generated materials
 */
const MaterialResultsCards = ({
    materials,
    mode,
    schoolSubjects,
    chaptersOptions,
    watchedSubject,
    watchedChapter,
    loadingStates,
    onCreateActivity,
    // Remplace cette carte seule par un énoncé neuf. Absent : pas de bouton.
    onReplaceCard,
    quizErrorStates = {},
    onClearQuizError,
    isPrerequisiteQuizEnabled = false,
    activityLevel = null,
}) => {
    const dimensions = useContext(DimensionsContext)
    const isSmallScreen = dimensions?.width < 800

    // Track prerequisite quiz toggle state per material
    const [prerequisiteQuizStates, setPrerequisiteQuizStates] = useState({})
    const [showQuizInfoModal, setShowQuizInfoModal] = useState(false)

    // Default quiz toggle ON for "Entraînement" (level 1) and "Révision du cours" (level 2)
    useEffect(() => {
        if (
            isPrerequisiteQuizEnabled &&
            (activityLevel === "1" || activityLevel === "2") &&
            materials.length > 0
        ) {
            const defaults = {}
            for (const m of materials) {
                if (m.id && m.status !== "pending") {
                    defaults[m.id] = true
                }
            }
            if (Object.keys(defaults).length > 0) {
                setPrerequisiteQuizStates((prev) => {
                    // Only set defaults for materials not already explicitly toggled
                    const updated = { ...prev }
                    for (const [id, val] of Object.entries(defaults)) {
                        if (!(id in updated)) {
                            updated[id] = val
                        }
                    }
                    return updated
                })
            }
        }
    }, [materials, isPrerequisiteQuizEnabled, activityLevel])

    const togglePrerequisiteQuiz = (materialId) => {
        setPrerequisiteQuizStates((prev) => ({
            ...prev,
            [materialId]: !prev[materialId],
        }))
        // Clear error when toggle changes
        if (onClearQuizError) {
            onClearQuizError(materialId)
        }
    }

    // Maintain a mapping of unique skillsText to a sequential color index
    const skillsColorMapRef = useRef({})
    const getSkillsBackgroundColor = (skillsText, baseColor) => {
        if (!skillsText || !baseColor) return theme.colors.lightMore

        const lightPercent = 0.4
        const hueShifts = [-10, 0, 10]
        if (!(skillsText in skillsColorMapRef.current)) {
            skillsColorMapRef.current[skillsText] = Object.keys(
                skillsColorMapRef.current
            ).length
        }
        const idx = skillsColorMapRef.current[skillsText]
        const shift = hueShifts[idx % hueShifts.length]

        return createColorVariation(baseColor, lightPercent, shift)
    }

    return (
        <>
        {/* Quiz Info Modal */}
        <ModalM
            visible={showQuizInfoModal}
            onDismiss={() => setShowQuizInfoModal(false)}
            style={{ maxWidth: 400, width: "90%" }}
        >
            <View style={{ padding: 20, alignItems: "center" }}>
                <ChampionImageM name="sam" variant="main" width={80} />
                <TextM fontSize={16} fontWeight="Bold" color={theme.colors.primary} style={{ marginTop: 10, marginBottom: 12 }}>
                    Quiz de prérequis
                </TextM>
                <View style={{ marginBottom: 16, alignSelf: "stretch" }}>
                    <BulletTextM text="Je pose 6 questions rapides à l'élève avant l'exercice" />
                    <BulletTextM text="Je vérifie que les notions de base sont acquises" />
                    <BulletTextM text="Tu es alerté si je détecte des lacunes importantes, pour adapter ta session" />
                </View>
                <Pressable
                    onPress={() => setShowQuizInfoModal(false)}
                    style={{
                        alignSelf: "center",
                        backgroundColor: theme.colors.primary,
                        borderRadius: 8,
                        paddingHorizontal: 24,
                        paddingVertical: 10,
                    }}
                >
                    <TextM fontSize={14} fontWeight="SemiBold" color={theme.colors.white}>
                        Compris
                    </TextM>
                </Pressable>
            </View>
        </ModalM>
        <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{
                width: isSmallScreen ? null : "100%",
            }}
            style={{ marginBottom: 20, width: "100%" }}
        >
            <View style={{ flexDirection: "row", width: "100%" }}>
                {materials.map((material, index) => {
                    // Check if this is a first_sub_exercise with multiple steps
                    const hasStacks = material.variation === 'first_sub_exercise' && material.num_clusters > 1
                    // Top-left badge is only used by the legacy similar-exo /
                    // proposition flows (variation === concept_review, …).
                    // The 3-cards generate flow shows nothing here — diversity
                    // is communicated via BatchTypeIndicator under the teal
                    // skill_part bar.
                    const badgeLabel = computeLegacyVariationLabel(material)
                    const batchTypeLabel = computeBatchTypeIndicator(material, materials)

                    return (
                    <View
                        key={material.id || index}
                        style={{
                            position: 'relative', // Required for absolute children
                            marginRight: 15,
                            flex: isSmallScreen ? null : 3,
                            width: isSmallScreen ? dimensions?.width - 100 : null,
                        }}
                    >

                        {/* Main card - rendered last, will be on top */}
                        <View
                            style={{
                                backgroundColor: theme.colors.white,
                                borderRadius: 10,
                                borderWidth: 1,
                                borderColor: theme.colors.lightMore,
                                padding: 15,
                                height: "100%",
                            }}
                        >
                        {material.status === "pending" ? (
                            <View style={{ alignItems: "center", paddingVertical: 30 }}>
                                <ActivityIndicator size="large" color={theme.colors.primary} />
                                <TextM style={{ marginTop: 10 }}>Génération en cours...</TextM>
                                {badgeLabel ? (
                                    <View style={{ marginTop: 8 }}>
                                        <BatchTypeIndicator label={badgeLabel} />
                                    </View>
                                ) : null}
                            </View>
                        ) : material.status === "error" ? (
                            <View style={{ alignItems: "center", paddingVertical: 30, paddingHorizontal: 10 }}>
                                <FontAwesome5 name="exclamation-triangle" size={24} color={theme.colors.error} />
                                <TextM style={{ marginTop: 10, color: theme.colors.error, textAlign: "center" }}>
                                    {material.error_message || "Erreur de génération"}
                                </TextM>
                                {onReplaceCard && (
                                    <IconButtonM
                                        icon="sync-alt"
                                        iconColor={theme.colors.primary}
                                        size={16}
                                        onPress={() => onReplaceCard(material)}
                                        accessibilityLabel="Réessayer"
                                    />
                                )}
                                {badgeLabel ? (
                                    <View style={{ marginTop: 8 }}>
                                        <BatchTypeIndicator label={badgeLabel} />
                                    </View>
                                ) : null}
                            </View>
                        ) : (
                            <>
                                {/* Header — hierarchical order:
                                    1. Chapter parent (context)
                                    2. Skill_part teal bar (notion)
                                    3. Difficulty + Type chips inline (metadata)
                                    4. Title (the exercise itself) */}
                                <View style={{ marginBottom: 5 }}>
                                    {/* 1. Chapter parent name */}
                                    <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 5 }}>
                                        {material.school_subject_id && (
                                            <IconImageM
                                                uri={schoolSubjects?.find(x => x.id === material.school_subject_id)?.icon}
                                                width={16}
                                                style={{ marginRight: 5 }}
                                            />
                                        )}
                                        <TextM
                                            fontSize={11}
                                            color={schoolSubjects?.find(x => x.id === material.school_subject_id)?.color}
                                            wrap
                                            numberOfLines={1}
                                            style={{ flex: 1 }}
                                        >
                                            {material.chapter_name}
                                        </TextM>
                                        {onReplaceCard && (
                                            <IconButtonM
                                                icon="sync-alt"
                                                iconColor={theme.colors.primary}
                                                size={14}
                                                style={{ margin: 0 }}
                                                onPress={() => onReplaceCard(material)}
                                                accessibilityLabel="Remplacer cet énoncé"
                                            />
                                        )}
                                    </View>

                                    {/* 2. Skill_part teal bar */}
                                    {material.material_key_skills && (
                                        <View
                                            style={{
                                                backgroundColor: getSkillsBackgroundColor(
                                                    material.material_key_skills,
                                                    material.school_subject?.color
                                                ),
                                                padding: 8,
                                                borderRadius: 6,
                                                marginBottom: 8,
                                            }}
                                        >
                                            <TextM fontSize={10} color={theme.colors.grey} wrap>
                                                {material.material_key_skills}
                                            </TextM>
                                        </View>
                                    )}

                                    {/* 3. Difficulty + Type/variation chips inline */}
                                    {(material.level || badgeLabel || batchTypeLabel) && (
                                        <View
                                            style={{
                                                flexDirection: "row",
                                                alignItems: "center",
                                                marginBottom: 8,
                                                flexWrap: "wrap",
                                                gap: 5,
                                            }}
                                        >
                                            {material.level && (
                                                <ActivityLevelBadge
                                                    level={material.level}
                                                    iconOnly={false}
                                                    style={{ minHeight: 20, marginTop: 0 }}
                                                />
                                            )}
                                            {badgeLabel && (
                                                <BatchTypeIndicator label={badgeLabel} />
                                            )}
                                            {batchTypeLabel && (
                                                <BatchTypeIndicator label={batchTypeLabel} />
                                            )}
                                        </View>
                                    )}

                                    {/* 4. Title */}
                                    <TextM fontSize={16} fontWeight="Bold" style={{ marginBottom: 5 }} wrap numberOfLines={2}>
                                        {material.title}
                                    </TextM>
                                </View>

                                {/* Content */}
                                <View style={{ flex: 1, marginBottom: 15 }}>
                                    <ScrollView
                                        style={{ backgroundColor: theme.colors.white, maxHeight: 350, flex: 1 }}
                                        contentContainerStyle={!IS_WEB ? { flexGrow: 1 } : {}}
                                        nestedScrollEnabled
                                    >
                                        <MaterialContentDisplay
                                            material={material}
                                            showBackgroundGenerationMessage={true}
                                            style={[
                                                isSmallScreen ? { width: "100%" } : {},
                                                !IS_WEB ? { flex: 1, width: "100%" } : {}
                                            ]}
                                            width={"100%"}
                                        />
                                    </ScrollView>
                                </View>

                                {/* Prerequisite Quiz Toggle - only shown if feature flag is enabled */}
                                {isPrerequisiteQuizEnabled && (
                                    <Pressable
                                        onPress={() => togglePrerequisiteQuiz(material.id)}
                                        style={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            backgroundColor: prerequisiteQuizStates[material.id]
                                                ? theme.colors.primaryLighter
                                                : theme.colors.lighter,
                                            borderRadius: 8,
                                            padding: 10,
                                            marginBottom: 10,
                                            borderLeftWidth: 3,
                                            borderLeftColor: theme.colors.primary,
                                            borderWidth: prerequisiteQuizStates[material.id] ? 1 : 0,
                                            borderColor: theme.colors.primary,
                                        }}
                                    >
                                        <View style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
                                            <FontAwesome5
                                                name="bolt"
                                                size={16}
                                                color={prerequisiteQuizStates[material.id]
                                                    ? theme.colors.primary
                                                    : theme.colors.grey
                                                }
                                                style={{ marginRight: 8 }}
                                            />
                                            <View style={{ flex: 1 }}>
                                                <View style={{ flexDirection: "row", alignItems: "center" }}>
                                                    <TextM
                                                        fontSize={13}
                                                        fontWeight={prerequisiteQuizStates[material.id] ? "SemiBold" : "Medium"}
                                                        color={prerequisiteQuizStates[material.id]
                                                            ? theme.colors.primary
                                                            : theme.colors.grey
                                                        }
                                                    >
                                                        Quiz de prérequis
                                                    </TextM>
                                                    <Pressable
                                                        onPress={(e) => {
                                                            e.stopPropagation()
                                                            setShowQuizInfoModal(true)
                                                        }}
                                                        hitSlop={8}
                                                        style={{ marginLeft: 6 }}
                                                    >
                                                        <FontAwesome5
                                                            name="info-circle"
                                                            size={13}
                                                            color={theme.colors.grey}
                                                        />
                                                    </Pressable>
                                                </View>
                                                <TextM
                                                    fontSize={9}
                                                    color={theme.colors.grey}
                                                    wrap
                                                >
                                                    Détecte les lacunes avant l'exercice
                                                </TextM>
                                            </View>
                                        </View>
                                        <Switch
                                            value={prerequisiteQuizStates[material.id] || false}
                                            onChange={() => togglePrerequisiteQuiz(material.id)}
                                            color={theme.colors.primary}
                                        />
                                    </Pressable>
                                )}

                                {/* Create Button */}
                                <CreateActivityButton
                                    onPress={() => onCreateActivity(material, prerequisiteQuizStates[material.id] || false)}
                                    loading={loadingStates[material.id]}
                                    isCreated={material.createStatus === "created"}
                                    style={{ alignSelf: "center", width: "90%" }}
                                />

                                {quizErrorStates[material.id] && (
                                    <View
                                        style={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            marginTop: 8,
                                            paddingHorizontal: 4,
                                        }}
                                    >
                                        <FontAwesome5
                                            name="exclamation-circle"
                                            size={12}
                                            color={theme.colors.error}
                                            style={{ marginRight: 6 }}
                                        />
                                        <TextM
                                            fontSize={10}
                                            color={theme.colors.error}
                                            wrap
                                            style={{ flex: 1 }}
                                        >
                                            Erreur lors de la génération du quiz. Réessayez ou désactivez l'option quiz.
                                        </TextM>
                                    </View>
                                )}
                            </>
                        )}
                    </View>
                    </View>
                    )
                })}
            </View>
        </ScrollView>
        </>
    )
}

export default MaterialResultsCards
